import { Doc } from '@dadohr/shared/domainTypes/document';
import { AtlassianIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/atlassian';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import ENVS from '../../../../../../env.json';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { PageOAuthRedirectProps } from '../../../../../OAuth/pages/Redirect';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import { OAuthSetupDialog } from '../oauth';
import { removeAtlassianConfig, useAtlassianConfig } from './service';

const NAME = 'Jira';

export const ATLASSIAN_OAUTH_REDIRECT_PROPS: PageOAuthRedirectProps = {
  integrationName: NAME,

  // TODO: FOR MIGRATION
  internalIntegrationName: 'atlassian',
  cloudFn: 'atlassian_exchangeCodeAndStoreCredentials'
};

const OAUTH = ENVS.atlassian.oauth;

// See: https://developer.atlassian.com/cloud/jira/platform/scopes-for-oauth-2-3LO-and-forge-apps/
const ATLASSIAN_SCOPES = [
  'offline_access',
  'read:jira-user',
  'read:jira-work',
  'read:servicedesk-request',
  'write:jira-work'
];

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const InitialSetupDialog = ({
  orgId,
  open,
  onClose
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const scopes = ATLASSIAN_SCOPES;
  const { t } = useTranslation();

  return (
    <OAuthSetupDialog
      orgId={orgId}
      open={open}
      onClose={onClose}
      name={NAME}
      oauthUrl={'https://auth.atlassian.com/authorize'}
      clientId={OAUTH.clientId}
      redirectUri={OAUTH.redirectUri}
      otherParams={{
        audience: 'api.atlassian.com',
        scope: scopes.join(' '),
        response_type: 'code',
        prompt: 'consent'
      }}
    >
      <Alert severity="info" icon={false}>
        {t('org.integrations.atlassian.setupInfo.p1')}
      </Alert>
      <Typography component="p" variant="body1">
        {t('org.integrations.atlassian.setupInfo.p2')}
      </Typography>
      <Alert severity="warning" icon={false}>
        {t('org.integrations.atlassian.setupInfo.p3')}
      </Alert>
    </OAuthSetupDialog>
  );
};

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<AtlassianIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('org.integrations.atlassian.setupInfo.p1')}
        </Alert>

        <IntegrationData>
          <strong>{t('org.integrations.common.accessToken')}</strong>
          <div>{masked(config.data.accessToken)}</div>
          <strong>{t('org.integrations.common.refreshToken')}</strong>
          <div>{masked(config.data.refreshToken)}</div>
        </IntegrationData>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeAtlassianConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

interface SetupDialogProps
  extends IntegrationSetupDialogWithConfigProperties<AtlassianIntegrationConfig> {
  orgId: string;
}

export const SetupDialog = ({
  config,
  onClose,
  orgId,
  onSuccessfulIntegration, // can be ignored since this integration is not an import source
  open
}: SetupDialogProps) => {
  const setup = !!(config?.data.accessToken && config?.data.refreshToken);
  return (
    <>
      {!setup && (
        <InitialSetupDialog orgId={orgId} open={open} onClose={onClose} />
      )}
      {setup && config && (
        <ExistingSetupDialog config={config} open={open} onClose={onClose} />
      )}
    </>
  );
};

export const AtlassianSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useAtlassianConfig(org.id, userId);
  const cfg = config || null;
  const active = !!cfg?.data.active;

  return (
    <>
      <IntegrationConfigurationButton
        functionalities={functionalities}
        name={NAME}
        logoUrl="/images/integrations-logos/jira.svg"
        active={active}
        interactive={interactive}
        renderSetupDialog={({
          open,
          onClose,
          onFailure,
          onSuccessfulIntegration
        }: IntegrationSetupDialogProperties) => (
          <SetupDialog
            config={cfg}
            orgId={org.id}
            open={open}
            onClose={onClose}
            onFailure={onFailure}
            onSuccessfulIntegration={onSuccessfulIntegration}
          />
        )}
      />
    </>
  );
};
